import {createElement, ReactElement} from "react";
import {TBody} from "./Table/TBody";
import {Tooltip} from "./Elements/Tooltip";
import {Copy, Link45deg, Search} from "react-bootstrap-icons";

export class Util {

    static getColorByStatus(status: string): string {
        switch (status) {
            case 'draft':
            case 'archived':
            case 'passive':
                return 'text-secondary'
            case 'on-hold':
            case 'scheduled':
            case 'cancelled':
            case 'expired':
                return 'text-warning'
            case 'SIGKILL':
            case 'SIGQUIT':
            case 'error':
            case 'inactive':
            case 'import-error':
            case 'close-error':
            case 'sync-error':
            case 'address-error':
            case 'create-error':
            case 'insufficient-quantity':
            case 'unknown-product':
            case 'duplication-error':
            case 'customer-error':
            case 'inactivated':
                return 'text-danger'
            case 'completed':
            case 'closed':
            case 'started':
            case 'active':
                return 'text-success'
            case 'in-transit':
            case 'b2b-in-transit':
                return 'text-info'
            default:
                return 'text-primary';
        }
    }


    static convertUTCToLocal(utcDateStr: string) {
        const utcDate = new Date(utcDateStr + " UTC");
        return utcDate.getFullYear() + "-" +
            String(utcDate.getMonth() + 1).padStart(2, '0') + "-" +
            String(utcDate.getDate()).padStart(2, '0') + " " +
            String(utcDate.getHours()).padStart(2, '0') + ":" +
            String(utcDate.getMinutes()).padStart(2, '0') + ":" +
            String(utcDate.getSeconds()).padStart(2, '0');
    }

    static timeAgo(timeInput: string | any, defaultColor: any = null): any {
        if (!timeInput) {
            return '-';
        }

        let seconds: number = ((new Date()).getTime() - (new Date(timeInput + 'Z')).getTime()) / 1000;

        const time_formats = [
            [60, 'seconds', 1, 'text-success'], // 60
            [120, '1 minute ago', '1 minute from now', 'text-success'], // 60*2
            [3600, 'minutes', 60, 'text-warning'], // 60*60, 60
            [7200, '1 hour ago', '1 hour from now', 'text-danger'], // 60*60*2
            [86400, 'hours', 3600, 'text-danger'], // 60*60*24, 60*60
            [172800, 'Yesterday', 'Tomorrow', 'text-danger'], // 60*60*24*2
            [604800, 'days', 86400, 'text-danger'], // 60*60*24*7, 60*60*24
            [1209600, 'Last week', 'Next week', 'text-danger'], // 60*60*24*7*4*2
            [2419200, 'weeks', 604800, 'text-danger'], // 60*60*24*7*4, 60*60*24*7
            [4838400, 'Last month', 'Next month', 'text-danger'], // 60*60*24*7*4*2
            [29030400, 'months', 2419200, 'text-danger'], // 60*60*24*7*4*12, 60*60*24*7*4
            [58060800, 'Last year', 'Next year', 'text-danger'], // 60*60*24*7*4*12*2
            [2903040000, 'years', 29030400, 'text-danger'], // 60*60*24*7*4*12*100, 60*60*24*7*4*12
            [5806080000, 'Last century', 'Next century', 'text-danger'], // 60*60*24*7*4*12*100*2
            [58060800000, 'centuries', 2903040000, 'text-danger'] // 60*60*24*7*4*12*100*20, 60*60*24*7*4*12*100
        ];
        const colorize: any = (text: any, seconds: number) => {
            if (seconds < -5) {
                return createElement('span', {className: 'text-danger'}, text);
            }
            if (seconds < 60) {
                return createElement('span', {className: 'text-success'}, text);
            }
            if (seconds < 600) {
                return createElement('span', {className: 'text-warning'}, text);
            }
            return createElement('span', {className: 'text-danger'}, text);
        };
        let token = 'ago', list_choice = 1;
        if (seconds === 0) {
            return 'Just now'
        }
        if (seconds < 0) {
            seconds = Math.abs(seconds);
            token = 'from now';
            list_choice = 2;
        }
        let i = 0, format = time_formats[i++];
        while (format) {
            if (seconds < format[0]) {
                if (typeof format[2] === 'string') {
                    if (defaultColor) {
                        return createElement('span', {className: defaultColor}, (format[list_choice] as string));
                    }
                    return colorize((format[list_choice] as string), seconds);
                } else {
                    if (defaultColor) {
                        return createElement('span', {className: defaultColor}, Math.floor(seconds / format[2]) + ' ' + format[1] + ' ' + token);
                    }
                    return colorize(Math.floor(seconds / format[2]) + ' ' + format[1] + ' ' + token, seconds);
                }
            }
            format = time_formats[i++];
        }
        if (defaultColor) {
            return createElement('span', {className: defaultColor}, seconds);
        }
        return colorize('centuries', seconds);
    }

    static alertTypeByStatus(status: string): string {
        switch (status) {
            case 'draft':
            case 'archived':
                return 'alert alert-secondary'
            case 'on-hold':
            case 'scheduled':
            case 'cancelled':
                return 'alert alert-warning'
            case 'inactive':
            case 'error':
            case 'import-error':
            case 'close-error':
            case 'sync-error':
            case 'address-error':
            case 'create-error':
            case 'unknown-product':
            case 'insufficient-quantity':
            case 'duplication-error':
            case 'customer-error':
                return 'alert alert-danger'
            case 'completed':
            case 'closed':
            case 'active':
                return 'alert alert-success'
            case 'in-transit':
            case 'b2b-in-transit':
                return 'alert alert-info'
            default:
                return 'alert alert-primary';
        }
    }

    static async copy(event: any) {
        event.preventDefault();

        let copyText = event.target.innerText;
        if (event.target.hasAttribute('data-copy')) {
            copyText = event.target.getAttribute('data-copy');
        }

        if (window.isSecureContext) {
            try {
                await navigator.clipboard.writeText(copyText);
            } catch (error) {
                // @ts-ignore
                console.error(error.message);
            }
        } else {
            const textArea = document.createElement("textarea");
            textArea.value = copyText;
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            try {
                document.execCommand('copy');
            } catch (err) {
                console.error('Unable to copy to clipboard', err);
            }
            document.body.removeChild(textArea);
        }

        event.target.classList.add('copyActive');

        setTimeout(function () {
            event.target.classList.contains('copyActive') && event.target.classList.remove('copyActive');
        }, 400);
    }

    static status(text: string, props: any = {}): ReactElement {
        const parentNode: string = props?.parentNode || 'span';

        if (props?.className) {
            props.className = props?.className + ' ' + this.getColorByStatus(text);
        } else {
            props.className = this.getColorByStatus(text);
        }

        props?.parentNode && delete props.parentNode;

        if (!props?.style) {
            props.style = {};
        }

        props.style.fontWeight = 700;
        props.style.textTransform = 'uppercase';

        if (props?.tooltip) {

            return (
                <Tooltip text={props.tooltip}>
                    {createElement(parentNode, props, text)}
                </Tooltip>
            );
        }

        return createElement(parentNode, props, text);
    }

    static link(text: string, href: string, props: any = {}): ReactElement {
        if (!props?.style) {
            props.style = {};
            props.style.textDecoration = 'none';
        }

        props.href = href;

        const linkLogo = (props.linkLogo || null) !== false;
        props.linkLogo && delete props.linkLogo;

        if (props?.tooltip) {
            return (
                <Tooltip text={props.tooltip}>
                    <a href={href} {...props}>{linkLogo && <Link45deg/>}{text}</a>
                </Tooltip>
            );
        }
        return (
            <a href={href} {...props}>{linkLogo && <Link45deg/>}{text}</a>
        );
    }

    static copyCell(text: string | any, props: any = {}): ReactElement {
        return (
            <td key={'rowFX' + TBody.index++} onClick={Util.copy} className="copy" {...props}>{text}</td>
        );
    }

    static copyIcon(copyText: string | number, props: any = {}): ReactElement {
        return (
            <Copy key={'rowFX' + TBody.index++} onClick={Util.copy} className="copy" data-copy={copyText} {...props}/>
        );
    }

    static copyCellWithIcon(text: string | any, props: any = {}): ReactElement {
        return (
            <td key={'rowFX' + TBody.index++} onClick={Util.copy} className="copy" {...props}>{text && <>
                <Copy/> {text}
            </>}</td>
        );
    }

    static searchCell(text: string | any, id: string | any, props: any = {}): ReactElement {
        return (
            <td key={'rowFX' + TBody.index++} onClick={Util.search}
                data-id={id} className="search" {...props}><Search/>{text}</td>
        );
    }


    static searchStatus(text: string | any, props: any = {}): ReactElement {
        props['data-id'] = 'status';
        props['data-search'] = text;
        return (
            <span key={'rowFX' + TBody.index++} onClick={Util.search}
                  data-id="status" className="search" data-search={text}>{Util.status(text, props)}</span>
        );
    }

    static async search(event: any) {
        event.preventDefault();

        let searchText = event.target.innerText;
        if (event.target.hasAttribute('data-search')) {
            searchText = event.target.getAttribute('data-search');
        }

        if (document.querySelector('input#' + event.target.getAttribute('data-id'))) {
            (document.querySelector('input#' + event.target.getAttribute('data-id')) as HTMLInputElement).value = searchText;
        } else if (document.querySelector('select#' + event.target.getAttribute('data-id'))) {
            (document.querySelector('select#' + event.target.getAttribute('data-id')) as HTMLSelectElement).value = searchText;
        }
        (document.querySelector('form.x-form button[type="submit"]') as HTMLButtonElement).click();
    }


    static async removeSearch(event: any) {
        event.preventDefault();
        if (document.querySelector('input#' + event.target.getAttribute('data-id'))) {
            (document.querySelector('input#' + event.target.getAttribute('data-id')) as HTMLInputElement).value = '';
        } else if (document.querySelector('select#' + event.target.getAttribute('data-id'))) {
            (document.querySelector('select#' + event.target.getAttribute('data-id')) as HTMLSelectElement).value = '';
        }
        (document.querySelector('form.x-form button[type="submit"]') as HTMLButtonElement).click();
    }

    static async removeAllSearch(event: any) {
        event.preventDefault();
        document.querySelectorAll('.deleteSearch').forEach((element) => {
            if (element.classList.contains('deleteSearchAll')) {
                return;
            }
            if (document.querySelector('input#' + element.getAttribute('data-id'))) {
                (document.querySelector('input#' + element.getAttribute('data-id')) as HTMLInputElement).value = '';
            } else if (document.querySelector('select#' + element.getAttribute('data-id'))) {
                (document.querySelector('select#' + element.getAttribute('data-id')) as HTMLSelectElement).value = '';
            }
        });
        (document.querySelector('form.x-form button[type="submit"]') as HTMLButtonElement).click();
    }

    static formatDate(date: Date | number): string {
        if (typeof date === 'number') {
            date = new Date(date);
        }
        return date.getFullYear() + "-" +
            String(date.getMonth() + 1).padStart(2, '0') + "-" +
            String(date.getDate()).padStart(2, '0') + " " +
            String(date.getHours()).padStart(2, '0') + ":" +
            String(date.getMinutes()).padStart(2, '0') + ":" +
            String(date.getSeconds()).padStart(2, '0');
    }
}
